export const en = {
  iptvProxy: {
    route: 'Request route',
    proxy: 'Stream server',
    direct: 'API server',
    enabled: 'Fetch through stream server',
    help: 'Use the configured relay for provider details and playlists, including channel automation.',
  },
  imageUpload: {
    preview: 'Image preview',
    openPreview: 'Open image preview',
  },
  radioCampaign: {
    "title": "Radio",
    "enabled": "Show on radio",
    "disabled": "Not shown on radio",
    "mode": "Radio screen mode",
    "BOTH": "Browsing and full screen",
    "BROWSING": "Browsing only",
    "FULLSCREEN": "Full screen only",
    "include": "Include stations",
    "exclude": "Exclude stations",
    "all": "All stations",
    "none": "None",
    "select": "Select stations",
    "targeting": "Leave included stations empty to target all stations. Exclusions take precedence.",
    "placement": "Banner and text ads appear below the station preview or in the lower full-screen area. News tickers appear along the bottom. Radio uses bottom placement at normal scale; TV/VOD placement settings remain independent.",
    "video": "Video ads are not supported on radio.",
    "loading": "Loading stations…",
    "loadError": "Unable to load stations. Existing selections are preserved.",
    "retry": "Retry",
    "overlap": "A station cannot be both included and excluded.",
    "location": "Geographic targeting uses the listener’s location, not the station’s country."
},
  // Form validation messages
  validation: {
    required: 'This field is required',
    email: 'Please enter a valid email address',
    minLength: (min: number) => `Must be at least ${min} characters`,
    maxLength: (max: number) => `Must be less than ${max} characters`,
    passwordRequirements: 'Password must contain at least one lowercase letter, one uppercase letter, and one number',
    usernameFormat: 'Username can only contain letters, numbers, and underscores',
    phoneFormat: 'Please enter a valid phone number',
    urlFormat: 'Please enter a valid URL',
  },

  // Form fields
  form: {
    username: 'Username',
    fullName: 'Full Name',
    email: 'Email',
    phoneNumber: 'Phone Number',
    password: 'Password',
    role: 'Role',
  },

  // Form placeholders
  placeholders: {
    username: 'Enter username',
    fullName: 'Enter full name',
    email: 'Enter email address',
    phoneNumber: 'Enter phone number',
    password: 'Enter password',
    selectRole: 'Select a role',
  },

  // Helper texts
  helpers: {
    password: 'Minimum 4 characters',
  },

  // Buttons
  buttons: {
    create: 'Create User',
    creating: 'Creating User...',
    cancel: 'Cancel',
    confirm: 'Confirm',
    signIn: 'Sign in',
    signingIn: 'Signing in...',
    addLink: 'Add Link',
    edit: 'Edit',
    delete: "Delete",
    retry: 'Retry',
  },

  // Roles
  roles: {
    User: 'User',
    Moderator: 'Moderator',
    Admin: 'Administrator',
  },

  // Form titles and descriptions
  createUser: {
    title: 'Create New User',
    description: 'Fill in the details to create a new user in the system.',
  },

  // Authentication
  auth: {
    login: {
      title: 'Sign in to your account',
      appTitle: '',
      appSubtitle: 'Admin Dashboard',
      loginFailed: 'Login Failed',
      defaultAdmin: 'Default Admin Account:',
      unexpectedError: 'An unexpected error occurred. Please try again.',
    },
    placeholders: {
      username: 'Enter your username',
      password: 'Enter your password',
    },
  },

  // Error messages
  errors: {
    createUserFailed: 'Failed to create user',
    createCategoryFailed: 'Failed to create category',
    updateCategoryFailed: 'Failed to update category',
    deleteCategoryFailed: 'Failed to delete category',
    unexpectedError: 'An unexpected error occurred',
    loadingError: 'Error loading data',
    // API errors
    networkError: 'Network error. Please check your connection and try again.',
    serverError: 'Server error occurred. Please try again later.',
    authenticationExpired: 'Your session has expired. Please log in again.',
  },

  //users
  users: {
    registration: 'Registered',
    by: 'By',
    createdBy: 'Created by',
    title: 'User Management',
    listTitle: 'User List',
    messages: {
      deleted: "User deleted successfully",
      confirmDelete: "Are you sure you want to delete this user?",
    },
    placeholders: {
      search: 'Search users...',
      fullName: 'Enter full name',
      username: 'Enter username',
      email: 'Enter email address',
      phoneNumber: 'Enter phone number',
    },
    validation: {
      roleRequired: 'Role is required',
    }
  },

  // Categories
  categories: {
    title: 'Category Management',
    description: 'Manage system categories for movies and TV shows.',
    createTitle: 'Create New Category',
    createDescription: 'Fill in the details to create a new category.',
    editTitle: 'Edit Category',
    editDescription: 'Modify the category details.',
    listTitle: 'Category List',

    // Fields
    fields: {
      description: 'Description',
      englishDescription: 'English Description',
      type: 'Type',
      displayOrder: 'Display Order',
      imageUrl: 'Image URL',
      active: 'Active',
      createdDate: 'Created Date',
      updatedDate: 'Updated Date',
      adultContent: 'Adult Content',
      sportsContent: 'Sports Content',
    },

    // Placeholders
    placeholders: {
      description: 'Enter description',
      englishDescription: 'Enter English description',
      selectType: 'Select a type',
      displayOrder: 'Enter display order',
      imageUrl: 'Enter image URL',
      search: 'Search categories...',
    },

    // Types
    types: {
      MOVIE: 'Movie',
      TV_SHOW: 'TV Show',
      DOCUMENTARY: 'Documentary',
      SPORTS: 'Sports',
      MUSIC: 'Music',
      TV_CHANNEL: 'TV Channel',
      RADIO_STATION: 'Radio Station',
    },

    // Actions
    actions: {
      create: 'Create Category',
      creating: 'Creating Category...',
      edit: 'Edit',
      delete: 'Delete',
      activate: 'Activate',
      deactivate: 'Deactivate',
      view: 'View Details',
    },

    // Messages
    messages: {
      created: 'Category created successfully',
      updated: 'Category updated successfully',
      deleted: 'Category deleted successfully',
      statusUpdated: 'Category status updated',
      confirmDelete: 'Are you sure you want to delete this category?',
      noResults: 'No categories found.',
      noData: 'No categories available.',
    },
  },

  // TV Guide
  tvGuide: {
    title: 'TV Channel Guide',
    description: 'Manage system TV channel guides.',
    createTitle: 'Create New TV Guide',
    createDescription: 'Fill in the details to create a new TV channel guide.',
    editTitle: 'Edit TV Guide',
    editDescription: 'Modify the TV channel guide details.',
    listTitle: 'TV Guide List',

    // Fields
    fields: {
      title: 'Title',
      url: 'URL',
      active: 'Active',
      gzip: 'GZIP Compression',
      lastSyncDate: 'Last Sync Date',
      lastSyncStatus: 'Last Sync Status',
      syncErrorMessage: 'Sync Error',
      createdDate: 'Created Date',
      updatedDate: 'Updated Date',
    },

    // Placeholders
    placeholders: {
      title: 'Enter guide title',
      url: 'Enter guide URL',
      search: 'Search TV guides...',
    },

    // Actions
    actions: {
      create: 'Create TV Guide',
      creating: 'Creating TV Guide...',
      edit: 'Edit',
      sync: 'Sync',
      delete: 'Delete',
      activate: 'Activate',
      deactivate: 'Deactivate',
      view: 'View Details',
    },

    // Messages
    messages: {
      created: 'TV guide created successfully',
      updated: 'TV guide updated successfully',
      deleted: 'TV guide deleted successfully',
      statusUpdated: 'TV guide status updated',
      confirmDelete: 'Are you sure you want to delete this TV guide?',
      noResults: 'No TV guides found.',
      noData: 'No TV guides available.',
    },

    // Validation
    validation: {
      titleRequired: 'Title is required',
      urlRequired: 'URL is required',
      urlInvalid: 'Please enter a valid URL',
    },
  },

  // TV Channels
  tvChannels: {
    title: 'TV Channels',
    description: 'Manage system television channels.',
    createTitle: 'Create New TV Channel',
    createDescription: 'Fill in the details to create a new television channel.',
    editTitle: 'Edit TV Channel',
    editDescription: 'Modify the television channel details.',
    listTitle: 'TV Channels',

    // Fields
    fields: {
      channelNumber: '#',
      country: 'Country',
      title: 'Title',
      tvGuide: 'TV Guide',
      category: 'Category',
      categories: 'Categories',
      epgId: 'EPG ID',
      imageUrl: 'Image',
      active: 'Active',
      play: 'Play',
      adultContent: 'Adult Content',
      kidsContent: 'Kids Content',
      urlType: 'URL Type',
      url: 'URL',
      createdDate: 'Created Date',
      updatedDate: 'Updated Date',
      logo: 'Logo'
    },

    // Placeholders
    placeholders: {
      channelNumber: 'Enter channel number',
      country: 'Select a country',
      title: 'Enter channel title',
      tvGuide: 'Select a TV guide',
      category: 'Select a category',
      categories: 'Select categories',
      epgId: 'Enter EPG ID',
      imageUrl: 'Enter image URL',
      url: 'Enter URL or number',
      search: 'Search TV channels...',
    },

    // URL Types
    urlTypes: {
      internal: 'Internal',
      external: 'External',
    },

    // Actions
    actions: {
      create: 'Create TV Channel',
      creating: 'Creating TV Channel...',
      edit: 'Edit',
      delete: 'Delete',
      activate: 'Activate',
      deactivate: 'Deactivate',
      play: 'Play',
      view: 'View Details',
    },

    // Messages
    messages: {
      created: 'TV channel created successfully',
      updated: 'TV channel updated successfully',
      deleted: 'TV channel deleted successfully',
      statusUpdated: 'TV channel status updated',
      confirmDelete: 'Are you sure you want to delete this TV channel?',
      noResults: 'No TV channels found.',
      noData: 'No TV channels available.',
      imagePreview: 'Image preview',
      imageError: 'Error loading image',
      resolvingStream: 'Resolving stream URL...',
      rtspInlineNotSupported: 'RTSP streams are not supported for in-browser playback.',
      noPlayableStream: 'No playable stream URL found.',
    },

    // Base URL Configuration
    baseUrl: {
      title: 'Channels Base URL',
      edit: 'Edit',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      loading: 'Loading...',
      placeholder: 'Enter channels base URL',
      notSet: 'Not set',
      saveSuccess: 'Channels base URL updated successfully',
      saveError: 'Failed to update channels base URL',
    },

    // Validation
    validation: {
      channelNumberRequired: 'Channel number is required',
      channelNumberMin: 'Channel number must be greater than or equal to 0',
      countryRequired: 'Country is required',
      titleRequired: 'Title is required',
      tvGuideRequired: 'TV guide is required',
      categoryRequired: 'Category is required',
      epgIdMax: 'EPG ID must be maximum 1000 characters',
      imageUrlInvalid: 'Please enter a valid image URL',
      urlRequired: 'URL is required',
      urlInvalidExternal: 'Please enter a valid external URL',
      urlInvalidInternal: 'Please enter a valid number for internal URL',
    },
  },

  countries: {
    title: 'Countries',
    description: 'Manage the catalog of countries used across the platform.',
    createTitle: 'Create Country',
    createDescription: 'Provide the information to create a new country entry.',
    editTitle: 'Edit Country',
    editDescription: 'Update the selected country information.',
    listTitle: 'Countries List',

    fields: {
      nameEnglish: 'English Name',
      name: 'Name',
      flag: 'Flag',
      displayOrder: 'Display Order',
      createdDate: 'Created Date',
      updatedDate: 'Updated Date',
    },

    placeholders: {
      nameEnglish: 'Enter English name',
      name: 'Enter country name',
      flag: 'Enter emoji or image URL',
      flagPreview: 'Flag preview',
      flagNotSet: 'No flag configured',
      displayOrder: 'Enter display order',
      displayOrderNotSet: 'Not set',
      search: 'Search countries...',
    },

    actions: {
      create: 'Create Country',
      creating: 'Saving country...',
      update: 'Update Country',
      edit: 'Edit',
      delete: 'Delete',
    },

    messages: {
      created: 'Country created successfully',
      updated: 'Country updated successfully',
      deleted: 'Country deleted successfully',
      confirmDelete: 'Are you sure you want to delete this country?',
      noData: 'No countries found. Start by creating one.',
    },

    validation: {
      nameEnglishRequired: 'English name is required',
      nameRequired: 'Name is required',
      nameMax: 'Name must be at most 150 characters',
      displayOrderNumber: 'Display order must be a whole number',
      displayOrderMin: 'Display order must be greater than or equal to 0',
    },
  },

  // Sports
  sports: {
    title: 'Sports Management',
    description: 'Manage sports content in the system.',
    createTitle: 'Create New Sport',
    createDescription: 'Fill in the details to create a new sports content.',
    editTitle: 'Edit Sport',
    editDescription: 'Modify the sports content details.',
    listTitle: 'Sports',

    // Fields
    fields: {
      title: 'Title',
      year: 'Year',
      releaseDate: 'Release Date',
      durationMinutes: 'Duration (Minutes)',
      spanishSynopsis: 'Spanish Synopsis',
      englishSynopsis: 'English Synopsis',
      genres: 'Genres',
      category: 'Category',
      coverImage: 'Cover Image',
      backgroundImage: 'Background Image',
      trailer: 'Trailer URL',
      reference: 'Reference',
      active: 'Active',
      adultContent: 'Adult Content',
      monoAudio: 'Mono Audio',
      englishOnly: 'English Only',
      kidsContent: 'Kids Content',
      createdDate: 'Created Date',
      updatedDate: 'Updated Date',
    },

    // Placeholders
    placeholders: {
      title: 'Enter sports title',
      year: 'Enter year',
      releaseDate: 'Select release date',
      durationMinutes: 'Enter duration in minutes',
      spanishSynopsis: 'Enter synopsis in Spanish',
      englishSynopsis: 'Enter synopsis in English',
      category: 'Select a category',
      coverImage: 'Enter cover image URL',
      backgroundImage: 'Enter background image URL',
      trailer: 'Enter trailer URL',
      reference: 'Enter reference',
      search: 'Search sports...',
    },
    // Genres
    genres: {
      AEROBICO: 'Aerobic',
      AERODROMO: 'Aerodrome',
      AJEDREZ: 'Chess',
      ALA_DELTA: 'Hang Gliding',
      ALPINISMO: 'Mountaineering',
      ARTES_MARCIALES: 'Martial Arts',
      ATLETISMO: 'Athletics',
      AUTOMOVILISMO: 'Motor Racing',
      BADMINTON: 'Badminton',
      BALONCESTO: 'Basketball',
      BEISBOL: 'Baseball',
      BOXEO: 'Boxing',
      CICLISMO: 'Cycling',
      ESCALADA: 'Climbing',
      ESGRIMA: 'Fencing',
      ESQUI: 'Skiing',
      FUTBOL: 'Soccer',
      FUTBOL_AMERICANO: 'American Football',
      GIMNASIA: 'Gymnastics',
      GOLF: 'Golf',
      HOCKEY: 'Hockey',
      LUCHA_LIBRE: 'Wrestling',
      NATACION: 'Swimming',
      MOTOCICLISMO: 'Motorcycling',
      PARACAIDISMO: 'Parachuting',
      RUGBY: 'Rugby',
      SURF: 'Surfing',
      TENIS: 'Tennis',
      VOLLEYBALL: 'Volleyball',
      WATERPOLO: 'Water Polo',
    },
    // Actions
    actions: {
      create: 'Create Sport',
      creating: 'Creating Sport...',
      edit: 'Edit',
      delete: 'Delete',
      activate: 'Activate',
      deactivate: 'Deactivate',
      view: 'View Details',
      addLink: 'Add Link',
    },

    // Messages
    messages: {
      created: 'Sport created successfully',
      updated: 'Sport updated successfully',
      deleted: 'Sport deleted successfully',
      statusUpdated: 'Sport status updated',
      confirmDelete: 'Are you sure you want to delete this sport?',
      noResults: 'No sports found.',
      noData: 'No sports available.',
      imagePreview: 'Image preview',
      imageError: 'Error loading image',
      linkAdded: 'Link added successfully',
      linkUpdated: 'Link updated successfully',
      linkDeleted: 'Link deleted successfully',
    },

    // Validation
    validation: {
      titleRequired: 'Title is required',
      titleMinLength: 'Title must be at least 3 characters',
      titleMaxLength: 'Title must not exceed 200 characters',
      yearRequired: 'Year is required',
      yearMin: 'Year must be at least 1900',
      yearMax: 'Year cannot exceed 2100',
      durationRequired: 'Duration is required',
      durationMin: 'Duration must be at least 1 minute',
      durationMax: 'Duration cannot exceed 999 minutes',
      spanishSynopsisMax: 'Spanish synopsis cannot exceed 10000 characters',
      englishSynopsisMax: 'English synopsis cannot exceed 10000 characters',
      categoryRequired: 'Category is required',
      coverImageInvalid: 'Please enter a valid cover image URL',
      backgroundImageInvalid: 'Please enter a valid background image URL',
      trailerInvalid: 'Please enter a valid trailer URL',
      referenceMax: 'Reference cannot exceed 100 characters'
    },
  },


  documentaries: {
    genres: {
      ARTE_Y_MUSICA: "Arte y Música",
      BIOGRAFIA: 'Biografía',
      CIENCIA: 'Ciencia',
      COMEDIA: 'Comedia',
      CONSPIRACION: 'Conspiración',
      CRIMEN: 'Crimen',
      DROGAS: 'Drogas',
      ECONOMIA: 'Economía',
      FILOSOFIA: 'Filosofía',
      DEPORTES: 'Deportes',
      HISTORIA: 'Historia',
      MEDIO_AMBIENTAL: 'Medio Ambiental',
      MEDIOS: 'Medios',
      MILITAR_Y_GUERRA: 'Militar y Guerra',
      MISTERIO: 'Misterio',
      NATURALEZA: 'Naturaleza',
      POLITICA: 'Política',
      PSICOLOGIA: 'Psicología',
      RELIGION: 'Religión',
      SALUD: 'Salud',
      SEXUALIDAD: 'Sexualidad',
      SOCIEDAD: 'Sociedad',
      TECNOLOGIA: 'Tecnología',
      VIAJES: 'Viajes',
    }
  },
  // Links
  links: {
    title: 'Sports Links',
    quality: 'Quality',
    url: 'URL',
    audioLanguage: 'Audio Language',
    subtitleLanguage: 'Subtitle Language',
    status: 'Status',
    actions: 'Actions',
    addLink: 'Add Link',
    editLink: 'Edit Link',
    deleteLink: 'Delete Link',
    noLinks: 'No links available',
    active: 'Active',
    inactive: 'Inactive',
    activate: 'Activate',
    deactivate: 'Deactivate',
    required: 'Add at least one link',
  },

  mediaAssets: {
    title: 'Background & Trailer Media',
    backgroundImageUrl: 'Background Image URL',
    backgroundImagePlaceholder: 'https://example.com/background.jpg',
    trailerUrl: 'Trailer URL',
    trailerUrlPlaceholder: 'https://youtube.com/watch?v=...',
    backgroundPreview: 'Background Preview',
    trailerPreview: 'Trailer Preview',
    failedToLoad: 'Failed to load',
    loadingBackgroundImage: 'Loading background image',
    backgroundImageFailedToLoad: 'Background image failed to load',
    playVideoInline: 'Play video inline',
    openTrailerInNewTab: 'Open trailer in new tab',
    clickToPlay: 'Click to play',
    clickToOpen: 'Click to open',
    youtubeVideo: 'YouTube Video',
    vimeoVideo: 'Vimeo Video',
    videoTrailer: 'Video Trailer',
    minimizeVideo: 'Minimize video',
    maximizeVideo: 'Maximize video',
    openVideoInNewTab: 'Open video in new tab',
    closeVideoPlayer: 'Close video player',
    videoFormatNotSupported: 'Video format not supported for inline playback',
    openInNewTab: 'Open in new tab',
    videoNotSupported: 'Your browser does not support the video tag.',
    emptyStateMessage: 'Add background image and trailer URLs to see previews',
    videoContent: 'video content'
  },

  movies: {
    genres: {
      ACTION: 'Action',
      ADVENTURE: 'Adventure',
      ANIMATION: 'Animation',
      BIOGRAPHY: 'Biography',
      COMEDY: 'Comedy',
      CRIME: 'Crime',
      DOCUMENTARY: 'Documentary',
      DRAMA: 'Drama',
      FAMILY: 'Family',
      FANTASY: 'Fantasy',
      HISTORY: 'History',
      HORROR: 'Horror',
      MUSIC: 'Music',
      MYSTERY: 'Mystery',
      ROMANCE: 'Romance',
      SCI_FI: 'Sci-Fi',
      TV_MOVIE: 'TV Movie',
      THRILLER: 'Thriller',
      WAR: 'War',
      WESTERN: 'Western',
    }
  },

  // Issue Reports
  issueReports: {
    title: 'Issue Reports',
    listTitle: 'Issue Reports',
    searchPlaceholder: 'Titles, Content, Type...',

    // Tabs
    tabs: {
      reportados: 'Reported',
      reparados: 'Fixed',
      todos: 'All'
    },

    // Table columns
    columns: {
      number: '#',
      title: 'Title',
      content: 'Content',
      date: 'Date',
      type: 'Type',
      status: 'Status',
      options: 'Options',
      occurrenceCount: 'Occurrences'
    },

    // Status labels
    status: {
      REPORTADO: 'Reported',
      EN_PROGRESO: 'In Progress',
      RESUELTO: 'Resolved',
      CERRADO: 'Closed',
      RECHAZADO: 'Rejected'
    },

    // Content types
    contentTypes: {
      TV: 'TV',
      MOVIE: 'Movie',
      DOCUMENTARY: 'Documentary',
      SPORT: 'Sports',
      CONCERT: 'Concert',
      EPISODE: 'Episode',
      TRAILER: 'Trailer'
    },

    // Issue types
    issueTypes: {
      VIDEO: 'Video',
      AUDIO: 'Audio',
      SUBTITLES: 'Subtitles',
      BUFFERING: 'Buffering',
      NOT_LOADING: 'Not Loading',
      INCORRECT_INFO: 'Incorrect Info',
      OTHER: 'Other'
    },

    // Priority levels
    priorities: {
      LOW: 'Low',
      MEDIUM: 'Medium',
      HIGH: 'High',
      CRITICAL: 'Critical'
    },

    // Actions
    actions: {
      view: 'View Content',
      edit: 'Edit',
      markAsResolved: 'Mark as resolved',
      markAsInProgress: 'Mark in progress',
      markAsRejected: 'Mark as rejected',
      assign: 'Assign',
      delete: 'Delete',
      changePriority: 'Change priority'
    },

    // Messages
    messages: {
      noResults: 'No issue reports found.',
      noData: 'No reports available.',
      resolved: 'Report marked as resolved',
      inProgress: 'Report marked as in progress',
      rejected: 'Report marked as rejected',
      assigned: 'Report assigned successfully',
      deleted: 'Report deleted successfully',
      priorityUpdated: 'Priority updated',
      confirmDelete: 'Are you sure you want to delete this report?',
      confirmResolve: 'Are you sure you want to mark this report as resolved?',
      confirmReject: 'Are you sure you want to reject this report?'
    },

    // Total display
    total: 'TOTAL:'
  },
  common: {
    active: 'Active',
    inactive: 'Inactive',
    actions: 'Actions',
    episodes: 'Episodes',
    confirmationTitle: 'Confirm action',
    fields: {
      createdDate: 'Created Date',
      updatedDate: 'Updated Date',
    }
  },

  // Under Construction
  underConstruction: {
    title: 'Page Under Construction',
    subtitle: 'We\'re working to bring you this functionality',
    description: 'This page is currently under development. We\'ll be back soon with amazing new features.',
    features: {
      title: 'Coming soon:',
      items: [
        'Advanced functionalities',
        'Intuitive and modern interface',
        'Enhanced integration',
        'Optimized user experience'
      ]
    },
    backButton: 'Back to home',
    contactInfo: 'If you have any questions, feel free to contact us.',
    estimatedTime: 'Estimated time: Coming soon'
  },

  radioStations: {
    title: 'Radio Stations',
    description: 'Manage system radio stations.',
    createTitle: 'Create New Radio Station',
    createDescription: 'Fill in the details to create a new radio station.',
    editTitle: 'Edit Radio Station',
    editDescription: 'Modify the radio station details.',
    listTitle: 'Radio Station List',

    // Fields
    fields: {
      stationName: 'Station Name',
      stationLogo: 'Logo',
      country: 'Country',
      categories: 'Categories',
      genres: 'Genres',
      streamUrl: 'Stream URL',
      active: 'Active',
      play: 'Play',
      createdDate: 'Created Date',
      updatedDate: 'Updated Date',
      code: "Code"
    },

    // Placeholders
    placeholders: {
      stationName: 'Enter station name',
      stationLogo: 'Enter logo URL',
      country: 'Enter country',
      categories: 'Select categories...',
      genres: 'Select genres...',
      streamUrl: 'Enter stream URL',
      search: 'Search radio stations...',
      allCategories: 'All categories',
      allCountries: 'All countries',
    },

    // Actions
    actions: {
      create: 'Create Radio Station',
      creating: 'Creating Radio Station...',
      edit: 'Edit',
      delete: 'Delete',
      activate: 'Activate',
      deactivate: 'Deactivate',
      play: 'Play',
    },

    // Messages
    messages: {
      created: 'Radio station created successfully',
      updated: 'Radio station updated successfully',
      deleted: 'Radio station deleted successfully',
      statusUpdated: 'Status updated successfully',
      confirmDelete: 'Are you sure you want to delete this radio station?',
      rtspInlineNotSupported: 'RTSP streams are not supported for in-browser playback.',
      noPlayableStream: 'No playable stream URL found.',
    },

    // Validation
    validation: {
      stationNameRequired: 'Station name is required',
      stationLogoInvalid: 'Please enter a valid logo URL',
      countryRequired: 'Country is required',
      genresRequired: 'At least one genre must be selected',
      streamUrlRequired: 'Stream URL is required',
      streamUrlInvalid: 'Please enter a valid stream URL',
    },

    // Base URL Configuration
    baseUrl: {
      title: 'Radio Stations Base URL Configuration',
      placeholder: 'https://example.com/radio',
      notSet: 'Not configured',
      edit: 'Edit',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      loading: 'Loading...',
      saveSuccess: 'Base URL updated successfully',
      saveError: 'Error updating base URL',
    },
  },
  tmdbSearch: {
    movieLabel: 'Search TMDB movie',
    tvShowLabel: 'Search TMDB TV show',
    placeholder: 'Enter a title or TMDB ID',
    helper: 'Select a result to load all available details.',
    startTyping: 'Start typing a title or TMDB ID.',
    noResults: 'No matching TMDB results found.',
    error: 'TMDB search could not be completed.',
    linkMovieLabel: 'Link this movie to TMDB',
    linkTvShowLabel: 'Link this TV show to TMDB',
    linkHelper: 'Matches are suggested from the IMDb ID first, then the title and year. Select the correct result and save.',
    episodeLinkHelper: 'Select the parent TV show. It will be linked to TMDB when the episode details are loaded.',
    linkSuccess: 'TV show linked to TMDB.',
  },
  bulkEpisodes: {
    back: 'Back to episodes',
    addEpisode: 'Add episode',
    deleteEpisodes: 'Delete episodes',
    deleteTitle: 'Select episodes to delete',
    deleteDescription: 'Select one or more episodes from this TV show.',
    deleteSelected: 'Delete selected',
    selectAll: 'Select all episodes',
    deleteConfirmation: 'Are you sure you want to delete {count} selected episodes? This action cannot be undone.',
    deleted: '{count} episodes deleted successfully',
    noneSelected: 'Select at least one episode to continue.',
    title: 'Bulk import episodes from TMDB',
    generatedTitle: 'Bulk create episodes',
    tmdbId: 'TMDB show ID',
    tmdbLinkHelp: 'Select the parent TV show. It will be linked to TMDB when the preview is loaded.',
    seasonFrom: 'Season from',
    seasonTo: 'Season to',
    episodeFrom: 'Episode from',
    episodeTo: 'Episode to',
    quality: 'Quality',
    linkTemplate: 'Link template',
    linkHelp: 'Use [ss]/[s] for season and [eee]/[ee]/[e] for episode.',
    audioLanguages: 'Audio languages',
    subtitleLanguages: 'Subtitle languages',
    englishTitleTemplate: 'English title template',
    spanishTitleTemplate: 'Spanish title template',
    titleTemplateHelp: 'Use # where the episode number should appear.',
    defaultDuration: 'Default minutes',
    preview: 'Load preview',
    generatePreview: 'Generate preview',
    previewTitle: 'Episodes to create',
    selected: '{selected} of {total} selected',
    existing: '{count} existing episodes omitted',
    loadingImdbIds: 'Loading IMDb IDs...',
    imdbIdsPending: 'Wait for the IMDb ID lookup to finish before creating the episodes.',
    imdbIdsError: 'Some IMDb IDs could not be loaded. Load the preview again to retry.',
    create: 'Create {count} episodes',
    created: '{count} episodes created successfully',
    noEpisodes: 'No new episodes were found in the selected range.',
    previewError: 'Unable to load the episode preview.',
    createError: 'Unable to create the episodes.',
    invalidRange: 'The start of a range cannot be greater than its end.',
    tooMany: 'A maximum of 200 episodes can be created or imported at once.',
    missingEpisodePlaceholder: 'The link template must contain [e], [ee], or [eee].',
    missingSeasonPlaceholder: 'A multi-season link template must contain [s] or [ss].',
    missingTitlePlaceholder: 'Both title templates must contain #.',
    invalidDuration: 'Duration must be between 1 and 300 minutes.',
    incompleteEpisode: 'Every selected episode needs a title, duration, and media URL.',
    invalidLinks: 'Fix or deselect the episodes with invalid media URLs before creating them.',
    validationPending: 'Wait for the media URL checks to finish before creating the episodes.',
    linkValidationError: 'The media URL could not be checked.',
    checkingLink: 'Checking media URL...',
    duplicatePosition: 'Two selected episodes use the same season and episode number.',
    include: 'Include episode',
    season: 'Season',
    episode: 'Episode',
    englishTitle: 'English title',
    spanishTitle: 'Spanish title',
    airDate: 'Air date',
    duration: 'Minutes',
    rating: 'Rating',
    url: 'Media URL',
    details: 'Details',
    synopsis: 'Synopsis',
    view: 'View',
    remove: 'Remove',
    detailsTitle: 'Episode details',
    coverImage: 'Cover image URL',
    englishSynopsis: 'English synopsis',
    spanishSynopsis: 'Spanish synopsis',
    done: 'Done',
  },
  vodPlayer: {
    title: 'Episode player',
    playEpisode: 'Play episode',
    movieTitle: 'Movie player',
    playMovie: 'Play movie',
    playLink: 'Play link',
    audioTrackUnavailable: 'The requested audio language was not found in this file.',
    subtitleTrackUnavailable: 'The requested subtitle language was not found in this file.',
    loading: 'Preparing secure playback...',
    trackSettings: 'Audio and subtitle settings',
    audioLanguage: 'Audio language',
    subtitles: 'Subtitles',
    off: 'Off',
    notSpecified: 'Not specified',
    noMedia: 'No media link is available for playback.',
    playbackError: 'The video could not be played. Check that the media URL is available and supported by this browser.',
    play: 'Play',
    pause: 'Pause',
    seek: 'Playback position',
    volume: 'Volume',
    fullscreen: 'Toggle fullscreen',
    track: 'Track',
  },
  contentProductivity: {
    title: 'Content productivity',
    subtitle: 'Monitor uploads, completed content, and contributor goals.',
    overview: 'Overview', goals: 'Goals and quotas', exportCsv: 'Export CSV',
    thisWeek: 'This week', lastWeek: 'Last week', thisMonth: 'This month', lastMonth: 'Last month',
    allContent: 'All content', movies: 'Movies', episodes: 'Episodes',
    allContributors: 'All contributors', contributor: 'Contributor', contributors: 'Contributors',
    filesUploaded: 'Files uploaded', contentCompleted: 'Content completed', uploadedSize: 'Uploaded size',
    dailyActivity: 'Daily upload activity', files: 'Files', completed: 'Completed',
    valid: 'Valid', invalid: 'Invalid', size: 'Size', type: 'Content type',
    loading: 'Loading…', noData: 'No uploads found for this period.',
    assignGoal: 'Assign goal', editGoal: 'Edit goal', selectContributor: 'Select a contributor',
    from: 'From', to: 'To', metric: 'Metric', target: 'Target', notes: 'Notes',
    frequency: 'Frequency', weekly: 'Weekly', monthly: 'Monthly',
    effectiveStart: 'Effective from', oneTimeLegacy: 'One-time (legacy)',
    assign: 'Assign', update: 'Update', cancel: 'Cancel', delete: 'Delete',
    targetUnits: 'target units', noGoals: 'No goals overlap this period.',
    quotaCreated: 'Goal assigned successfully.', quotaUpdated: 'Goal updated successfully.',
    quotaDeleted: 'Goal deleted.', confirmDelete: 'Delete this goal?',
    saveError: 'The goal could not be saved.', deleteError: 'The goal could not be deleted.',
    all: 'All', searchContent: 'Search by content title…',
    noDetails: 'No matching content was found.', inactive: 'Inactive', view: 'View',
    previous: 'Previous', next: 'Next', results: '{count} results',
    completionPercentage: 'Completion'
  },
  applicationSettings: {
    sectionsUi: {
      "administration": "Administration",
      "android": "Android TV",
      "security": "Security",
      "adminDescription": "Controls the administrative portal. Save changes separately in each section.",
      "androidDescription": "Global defaults for Android TV. User and app-specific overrides take precedence.",
      "loading": "Loading settings…",
      "retry": "Retry",
      "unsaved": "Unsaved changes",
      "discard": "Discard unsaved changes in this section?",
      "recordId": "Settings record",
      "defaultValue": "Default",
      "topCenter": "Top center",
      "noTheme": "Default app theme",
      "missingAdmin": "Administrative settings have not been configured.",
      "sections": {
            "branding": "Branding & appearance",
            "support": "Support & legal",
            "modules": "Content modules",
            "tools": "Administrative tools",
            "catalog": "Catalog & indexing",
            "demos": "Demo accounts",
            "appearance": "Home & theme",
            "navigation": "Navigation & playback",
            "movies": "Movies",
            "tvShows": "TV shows",
            "documentaries": "Documentaries",
            "sports": "Sports",
            "concerts": "Concerts"
      },
      "fields": {
            "appName": "Application name",
            "appSubtitle": "Subtitle",
            "logoUrl": "Logo URL",
            "logoCompactUrl": "Compact logo URL",
            "faviconUrl": "Favicon URL",
            "primaryColor": "Primary color",
            "secondaryColor": "Secondary color",
            "accentColor": "Accent color",
            "fontFamily": "Font family",
            "supportEmail": "Support email",
            "legalUrl": "Terms URL",
            "privacyUrl": "Privacy policy URL",
            "categoriesEnabled": "Categories",
            "tvChannelsEnabled": "Live TV channels",
            "tvGuideEnabled": "TV guide",
            "radioStationsEnabled": "Radio stations",
            "moviesEnabled": "Movies",
            "tvShowsEnabled": "TV shows",
            "documentariesEnabled": "Documentaries",
            "sportsEnabled": "Sports",
            "concertsEnabled": "Concerts",
            "iptvProvidersEnabled": "IPTV providers",
            "flussonicServersEnabled": "Flussonic servers",
            "channelAutomationsEnabled": "Channel automations",
            "channelScrapingEnabled": "Channel scraping",
            "liveTvCountriesEnabled": "Countries in live TV administration",
            "radioCountriesEnabled": "Countries in radio administration",
            "fullAddressEnabled": "Full user address",
            "adsEnabled": "Advertising",
            "reportsEnabled": "Reports",
            "premiereContentDays": "Premiere period (days)",
            "recentContentDays": "Recently added period (days)",
            "indexOnSaveOrUpdate": "Update search index when saving content",
            "maxDemoPerMonth": "Maximum demos per month",
            "demoDays": "Demo duration (days)",
            "layout": "Home layout",
            "themeId": "Android TV theme",
            "homeSlidesDuration": "Home slide duration (seconds; 0 = default)",
            "homeSlidesIndicatorPosition": "Slide indicator position",
            "changePasswordEnabled": "Allow password changes",
            "fullScreenEnterActsAsBack": "Use Enter to go back from full screen",
            "showChannelsOnCategoryFocus": "Show channels when a category gains focus",
            "channelCategoriesExpanded": "Expand channel categories",
            "categoryMenuOnPreviewMode": "Show category menu in preview mode",
            "numericButtonInCategoryMenuOnPreviewMode": "Show numeric button in preview category menu",
            "mixCategoriesAndCountriesAlphabetically": "Mix categories and countries alphabetically",
            "removeCountryGrouping": "Remove country grouping",
            "moviesLastNYears": "Release year window (years; 0 = all)",
            "moviesShowPremiere": "Show premieres",
            "moviesShowRecentlyAdded": "Show recently added",
            "tvShowsLastNYears": "Release year window (years; 0 = all)",
            "tvShowsShowPremiere": "Show premieres",
            "tvShowsShowRecentlyAdded": "Show recently added",
            "documentariesLastNYears": "Release year window (years; 0 = all)",
            "documentariesShowPremiere": "Show premieres",
            "documentariesShowRecentlyAdded": "Show recently added",
            "sportsLastNYears": "Release year window (years; 0 = all)",
            "sportsShowPremiere": "Show premieres",
            "sportsShowRecentlyAdded": "Show recently added",
            "concertsLastNYears": "Release year window (years; 0 = all)",
            "concertsShowPremiere": "Show premieres",
            "concertsShowRecentlyAdded": "Show recently added"
      }
},
    title: 'Application settings',
    layout: 'Home layout',
    save: 'Save settings',
    saved: 'Application settings saved.',
    loadError: 'The application settings could not be loaded.',
    saveError: 'The application settings could not be saved.',
    seconds: 'seconds',
    minutes: 'minutes',
    requests: 'requests',
    items: 'items',
    vodHarvesting: {
      title: 'VOD harvesting protection',
      description: 'Detect machine-speed play URL requests across every session and device of a user account. Changes take effect without restarting the API.',
      detectionEnabled: 'Enable harvesting detection',
      detectionHelp: 'When disabled, requests are neither evaluated nor blocked.',
      enforcementEnabled: 'Block detected harvesting',
      enforcementHelp: 'Keep this off for shadow mode: suspicious requests are logged but allowed.',
      thresholds: 'Detection thresholds',
      moreInformation: 'More information',
      shortWindow: 'Short content window',
      shortWindowHelp: 'Time span used to count how many different VOD items the account requests across all sessions and devices. For example, 30 counts distinct items requested during the latest 30 seconds.',
      requestWindow: 'Request-rate window',
      requestWindowHelp: 'Time span used for the short request-rate limit. Every VOD play URL request counts, including repeated requests for the same item.',
      longWindow: 'Long activity window',
      longWindowHelp: 'Longer observation period used by both long-window limits. It detects slower harvesting that stays below the short-window thresholds.',
      holdDuration: 'Temporary block duration',
      holdDurationHelp: 'How long play URL requests from every session and device of the detected account return HTTP 429 after a threshold is exceeded. Other API features remain available.',
      maxRequestsWindow: 'Maximum requests in request-rate window',
      maxRequestsWindowHelp: 'Allowed number of play URL requests within the request-rate window. The next request above this value triggers detection.',
      maxDistinctShort: 'Maximum distinct content in short window',
      maxDistinctShortHelp: 'Allowed number of different VOD items within the short content window. Repeated requests for one item count as a single distinct item.',
      maxRequestsLong: 'Maximum requests in long window',
      maxRequestsLongHelp: 'Allowed total number of play URL requests during the long activity window. The next request above this value triggers detection.',
      maxDistinctLong: 'Maximum distinct content in long window',
      maxDistinctLongHelp: 'Allowed number of different VOD items requested during the long activity window. This catches gradual catalog harvesting.',
    },
    layouts: {
      default: 'Default',
      verticalMenu: 'Vertical menu',
      hospitality: 'Hotels and residences',
      modern: 'Modern',
    },
  },
  contentPlanning: {
    title: 'Content planning',
    subtitle: 'Assign and track movies, episodes, and whole-show upload work.',
    create: 'Create task', createTitle: 'Create upload task', created: 'Task created.',
    updated: 'Task updated.', saveError: 'The task could not be saved.',
    search: 'Search tasks…', myTasks: 'My tasks', allStatuses: 'All statuses',
    allTypes: 'All types', allPriorities: 'All priorities', loading: 'Loading…',
    empty: 'No tasks match these filters.', previous: 'Previous', next: 'Next',
    assign: 'Assign', unassigned: 'Unassigned', start: 'Start', block: 'Block',
    resume: 'Resume', complete: 'Complete', blockedReasonPrompt: 'Why is this task blocked?',
    type: 'Content type', taskTitle: 'Title', description: 'Description',
    priority: 'Priority', dueDate: 'Due date', assignee: 'Assignee',
    tmdbId: 'TMDB ID', showTmdbId: 'Show TMDB ID', season: 'Season',
    episode: 'Episode', cancel: 'Cancel', selectTmdbContent: 'Search TMDB content',
    selectShow: 'Search parent TV show', loadEpisode: 'Load episode',
    tmdbError: 'TMDB details could not be loaded.',
    createEpisodeRange: 'Create an episode work package',
    createEpisodeRangeHelp: 'Preview a range and create one grouped task with an individual task for each selected episode.',
    seasonFrom: 'Season from', seasonTo: 'Season to',
    episodeFrom: 'Episode from', episodeTo: 'Episode to',
    previewEpisodes: 'Preview', include: 'Include', airDate: 'Air date',
    availability: 'Status', available: 'Available', alreadyUploaded: 'Already uploaded',
    alreadyPlanned: 'Already planned', uploaded: 'Uploaded', planned: 'Planned',
    ready: 'Ready', createSelected: 'Create selected tasks',
    batchCreated: '{count} episode tasks created.',
    episodeProgress: '{completed}/{total} episodes completed',
    workPackage: 'Work package', packageEpisode: 'Package episode',
    types: { MOVIE: 'Movie', EPISODE: 'Episode', TV_SHOW: 'Whole TV show' },
    status: {
      TODO: 'To do', IN_PROGRESS: 'In progress', BLOCKED: 'Blocked',
      COMPLETED: 'Completed', CANCELLED: 'Cancelled'
    },
    priorities: { LOW: 'Low', NORMAL: 'Normal', HIGH: 'High', URGENT: 'Urgent' }
  }
};
